import {Observer} from "mobx-react";
import type {IModel} from "../../../../data/models/Model";
import type {Type} from "../../../../data/models/Type";
import {featureTitles} from "../../../../data/state/AppStateConstants";
import type {Xyicon} from "../../../../data/models/Xyicon";
import {XyiconFeature} from "../../../../generated/api/base";
import type {Space} from "../../../../data/models/Space";
import type {Portfolio} from "../../../../data/models/Portfolio";
import type {Boundary} from "../../../../data/models/Boundary";
import type {Catalog} from "../../../../data/models/Catalog";
import {SelectInputV5} from "../../input/select/SelectInputV5";
import {HorizontalAlignment} from "../../../../utils/dom/DomUtils";
import {ConfirmWindowV5} from "../../popup/ConfirmWindowV5";
import type {IInputPropsV5} from "../../../widgets/input/clicktoedit/InputUtils";
import {useAppStore} from "../../../../StateManager";
import {XyiconModelFieldV5} from "./XyiconModelFieldV5";

interface ITypeInputProps extends IInputPropsV5 {
	readonly item?: IModel;
	readonly selectedItems?: IModel[];
}

export const TypeInputV5 = (props: ITypeInputProps) => {
	const appState = useAppStore((state) => state.appState);
	const {item, selectedItems, field, value, disabled} = props;

	const onTypeChange = async (type: Type) => {
		const title = `Confirm ${featureTitles[item.ownFeature]} Type Change`;
		const config = {
			ok: "Change",
			cancel: "Cancel",
		};
		let xyicons: Xyicon[] = [];
		let message = `Once you change the ${featureTitles[item.ownFeature].toLowerCase()} type, fields (and data) not assigned to the new ${featureTitles[item.ownFeature].toLowerCase()} type will be removed. Do you wish to continue?`;
		const items = selectedItems || [item];

		if (item.ownFeature === XyiconFeature.XyiconCatalog) {
			const affectedModels = items.filter((item) => item.typeId !== type.id).map((item) => (item as Catalog).model);

			xyicons = appState.actions.getList<Xyicon>(XyiconFeature.Xyicon).filter((xyicon) => affectedModels.includes(xyicon.model));
			message = `Once you change the catalog type, fields (and data) not assigned to the new catalog type will be removed. There are ${xyicons.length} xyicons that will be updated with the new catalog type and assigned fields.  Do you wish to continue?`;
		}

		const confirmed = await ConfirmWindowV5.open(message, title, config);

		if (confirmed && items) {
			// in case of SpaceEditor table boundary type change (selectedItems is empty) or if some items are selected and other element's type is changed
			const itemsToCheck = items.length > 0 && items.includes(item) ? items : [item];

			itemsToCheck.forEach((element) => {
				if (element.ownFeature === XyiconFeature.Space) {
					(element as Space).setSpaceType(type.id);
				} else if ([XyiconFeature.Portfolio, XyiconFeature.Boundary, XyiconFeature.XyiconCatalog].includes(element.ownFeature)) {
					element.typeId = type.id;
				}
			});

			if (item.ownFeature === XyiconFeature.Space) {
				await appState.app.transport.updateSpaceType(itemsToCheck as Space[]);
			} else if (item.ownFeature === XyiconFeature.Portfolio) {
				await appState.app.transport.updatePortfolioType(itemsToCheck as Portfolio[]);
			} else if (item.ownFeature === XyiconFeature.Boundary) {
				await appState.app.transport.updateBoundaryType(itemsToCheck as Boundary[]);
			} else if (item.ownFeature === XyiconFeature.XyiconCatalog) {
				await appState.app.transport.updateCatalogType(itemsToCheck as Catalog[]);
			}
		}
	};

	const itemFeature = field.feature;
	const fieldRefId = field.refId;
	const shouldBeXyiconModelField = itemFeature === XyiconFeature.Xyicon && fieldRefId.includes("model");

	return (
		<Observer>
			{() => {
				return shouldBeXyiconModelField ? (
					<XyiconModelFieldV5
						disabled={false}
						xyicons={(selectedItems as Xyicon[]) ?? []}
						item={item as Xyicon}
						inline={true}
					/>
				) : (
					<SelectInputV5
						options={appState.types[itemFeature === XyiconFeature.XyiconCatalog ? XyiconFeature.Xyicon : field.feature]}
						renderOptionWhenClosed={(item: IModel) => item.name}
						selected={appState.actions.getTypeById(value)}
						onChange={onTypeChange}
						horizontalAlignment={HorizontalAlignment.center}
						dropdownFixedWidth="272px"
						disabled={disabled}
					/>
				);
			}}
		</Observer>
	);
};
